import SlsTracker from "@aliyun-sls/web-track-browser";
import json from "./version.json";
import shortid from "shortid";

const win = globalThis as any;

let cachedClientId = "";
const CLIENT_ID_KEYS = ["clientId", "jianguoke_client_id"];
const TOKEN_KEYS = ["token"];
const USER_KEYS = ["user"];
const CLIENT_ID_MAX_AGE = 365 * 24 * 60 * 60;

const safeStorageGet = (storage: Storage | undefined, key: string) => {
  try {
    return storage?.getItem(key) || null;
  } catch {
    return null;
  }
};

const safeStorageSet = (storage: Storage | undefined, key: string, value: string) => {
  try {
    storage?.setItem(key, value);
  } catch {
    // ignore storage failures
  }
};

const readCookie = (name: string) => {
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}=([^;]*)`),
  );
  return match ? decodeURIComponent(match[1]) : null;
};

const resolveCookieDomain = (hostname: string) => {
  if (!hostname || hostname === "localhost" || /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)) {
    return null;
  }

  const parts = hostname.split(".");
  if (parts.length < 2) {
    return null;
  }

  return `.${parts.slice(-2).join(".")}`;
};

const writeCookie = (name: string, value: string) => {
  const segments = [
    `${name}=${encodeURIComponent(value)}`,
    "path=/",
    `max-age=${CLIENT_ID_MAX_AGE}`,
    "SameSite=Lax",
  ];
  const domain = resolveCookieDomain(window.location.hostname);

  if (domain) {
    segments.push(`domain=${domain}`);
  }
  if (window.location.protocol === "https:") {
    segments.push("Secure");
  }

  document.cookie = segments.join("; ");
};

const persistClientId = (clientId: string) => {
  CLIENT_ID_KEYS.forEach((key) => {
    safeStorageSet(window.localStorage, key, clientId);
    writeCookie(key, clientId);
  });
};

const safeParseJson = (raw: string | null) => {
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const decodeJwtPayload = (token?: string | null) => {
  if (!token || token.split(".").length < 2) {
    return null;
  }

  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = `${base64}${"=".repeat((4 - (base64.length % 4)) % 4)}`;
    const binary = window.atob(padded);
    const bytes = Array.from(binary, (char) =>
      `%${char.charCodeAt(0).toString(16).padStart(2, "0")}`,
    ).join("");

    return JSON.parse(decodeURIComponent(bytes));
  } catch {
    return null;
  }
};

const getCurrentUserId = () => {
  const explicitUserId = (globalThis as any).__JIANGUOKE_BEHAVIOR_USER_ID__;
  if (explicitUserId !== undefined && explicitUserId !== null && explicitUserId !== "") {
    return String(explicitUserId);
  }

  for (const key of TOKEN_KEYS) {
    const token =
      safeStorageGet(window.localStorage, key) ||
      safeStorageGet(window.sessionStorage, key) ||
      readCookie(key);
    const payload = decodeJwtPayload(token);
    const userId = payload?.id ?? payload?.userId ?? payload?.uid;
    if (userId !== undefined && userId !== null && userId !== "") {
      return String(userId);
    }
  }

  for (const key of USER_KEYS) {
    const user = safeParseJson(
      safeStorageGet(window.localStorage, key) ||
        safeStorageGet(window.sessionStorage, key),
    );
    const userId = user?.id ?? user?.userId ?? user?.uid;
    if (userId !== undefined && userId !== null && userId !== "") {
      return String(userId);
    }
  }

  return undefined;
};

export function getClientId() {
  if (cachedClientId) return cachedClientId;
  let clientId: string | null = null;
  for (const key of CLIENT_ID_KEYS) {
    clientId = readCookie(key) || safeStorageGet(window.localStorage, key);
    if (clientId) {
      break;
    }
  }
  if (!clientId) {
    clientId = shortid.generate();
  }
  persistClientId(clientId);
  cachedClientId = clientId;
  return clientId;
}

function syncIdentityTags() {
  const userId = getCurrentUserId();
  const { userId: _userId, ...currentTags } = tracker.getOpt().tags as Record<string, string>;
  tracker.getOpt().tags = {
    ...currentTags,
    clientId: getClientId(),
    ...(userId ? { userId } : {}),
  };
}

const initialUserId = getCurrentUserId();

const opts = {
  host: "cn-zhangjiakou.log.aliyuncs.com", // 所在地域的服务入口。例如cn-hangzhou.log.aliyuncs.com
  project: "jianguoke", // Project名称。
  logstore: "uba", // Logstore名称。
  time: 10, // 发送日志的时间间隔，默认是10秒。
  count: 10, // 发送日志的数量大小，默认是10条。
  topic: "type", // 自定义日志主题。
  source: win.location.hostname,
  tags: {
    ua: navigator.userAgent,
    referer: document.referrer,
    clientId: getClientId(),
    ...(initialUserId ? { userId: initialUserId } : {}),
  } as Record<string, string>,
};
const tracker = new SlsTracker(opts);
const _isDev = /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);

export function setDefaults(obj: any) {
  syncIdentityTags();
  tracker.getOpt().tags = {
    ...tracker.getOpt().tags,
    ...Object.keys(obj).reduce((acc: any, key) => {
      if (obj[key] !== undefined && obj[key] !== null) {
        acc[key] = obj[key].toString();
      }
      return acc;
    }, {}),
  };
}

/**
 * 记录用户行为
 *
 * @export
 * @param {string} eventName
 * @param {{ action: string; name: string; value: any }} data
 */
export function trace(
  eventName: string,
  data: { name: string; value?: any; [key: string]: any }
) {
  if (_isDev) return;
  // console.log(eventName, data);
  syncIdentityTags();
  tracker.send({
    eventName,
    page: document.title,
    ...Object.keys(data).reduce((acc: any, key) => {
      if (data[key] !== undefined && data[key] !== null) {
        acc[key] = data[key].toString();
      }
      return acc;
    }, {}),
  });
}

export function useClickTrace(
  name: string,
  onClick?: (e?: any) => void,
  value?: any
) {
  function handleClick(...params: any) {
    trace("click", { name, value });
    if (onClick) {
      onClick(...params);
    }
  }
  return { onClick: handleClick };
}

export function useChangeTrace(name: string, onChange?: (e?: any) => void) {
  function handleChange(changedValue: any) {
    trace("change", { name, value: changedValue });
    if (onChange) {
      onChange(changedValue);
    }
  }
  return { onChange: handleChange };
}

// ── 进入/离开页面埋点 ──
let _behaviorInitialized = false;
let _isPageActive = false;

function _initializeBehavior() {
  if (_behaviorInitialized) return;
  _behaviorInitialized = true;
  setDefaults(json);
}

function _trackEnter(trigger: string) {
  if (_isPageActive) return;
  _isPageActive = true;
  trace("open", { name: "进入页面", trigger });
}

function _trackLeave(trigger: string) {
  if (!_isPageActive) return;
  _isPageActive = false;
  trace("leave", { name: "离开页面", trigger });
}

if (document.visibilityState !== "hidden") {
  if (document.readyState === "loading") {
    win.document.addEventListener(
      "DOMContentLoaded",
      () => {
        _initializeBehavior();
        _trackEnter("DOMContentLoaded");
      },
      { once: true },
    );
  } else {
    _initializeBehavior();
    _trackEnter("readyState");
  }
}

// ── 全局错误捕获 ──
window.addEventListener("error", (e) => {
  trace("error", {
    name: "js_error",
    message: (e.message || "").slice(0, 500),
    value: (e.filename || "") + ":" + (e.lineno || 0) + ":" + (e.colno || 0),
  });
});

window.addEventListener("unhandledrejection", (e) => {
  const r = e.reason;
  const msg = r instanceof Error ? r.message : String(r || "");
  trace("error", { name: "promise_error", message: msg.slice(0, 500) });
});

document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "hidden") {
    _trackLeave("visibilitychange");
  } else {
    _trackEnter("visibilitychange");
  }
});
window.addEventListener("pageshow", () => _trackEnter("pageshow"));
window.addEventListener("pagehide", () => _trackLeave("pagehide"));

// ── 外链跳转埋点 ──
document.addEventListener("click", (e) => {
  const a = (e.target as HTMLElement).closest?.("a[href]") as HTMLAnchorElement;
  if (!a) return;
  const href = a.getAttribute("href") || "";
  if (href.startsWith("http") && !href.startsWith(window.location.origin)) {
    trace("leave", { name: "跳转外链", value: href, trigger: "click" });
  }
});

// ── 网络状态变化 ──
window.addEventListener("offline", () => {
  trace("network", { name: "offline", value: "disconnected" });
});
window.addEventListener("online", () => {
  trace("network", { name: "online", value: "reconnected" });
});

// ── 页面加载性能 ──
window.addEventListener("load", () => {
  setTimeout(() => {
    const nav = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming;
    if (nav) {
      trace("performance", {
        name: "page_load",
        value: String(Math.round(nav.loadEventEnd - nav.startTime)),
        dns: String(Math.round(nav.domainLookupEnd - nav.domainLookupStart)),
        tcp: String(Math.round(nav.connectEnd - nav.connectStart)),
        ttfb: String(Math.round(nav.responseStart - nav.requestStart)),
        dom_ready: String(Math.round(nav.domContentLoadedEventEnd - nav.startTime)),
      });
    }
  }, 0);
});
